/*
 * <BeMenuField> -- React wrapper for the .be-menu-field CSS component: the BeOS
 * R5 "dropdown" (a label + a pop-up menu in a recessed well with a down-triangle
 * marker). This is the CLOSED control; the open pop-up list is still native OS
 * chrome (see the TODO in components/menu-field.css).
 *
 * The well is the R5 two-layer composite (BMenuField frame + the inner
 * B_BORDER_CONTENTS menu-bar bevel); the marker is from the R5 _BMCItem_::Draw
 * decomp (see components/menu-field.css + Notes/18 for provenance). The
 * browser <select> is the picker engine -- pass <option>s as children.
 *
 *   <BeMenuField label="Connect to:" value={v} onValueChange={set}>
 *     <option value="primary">primary</option>
 *     <option value="work">work</option>
 *   </BeMenuField>
 */

import type { ChangeEvent, CSSProperties, ReactElement, ReactNode, RefAttributes } from "react";
import { type BeProps, beComponent } from "./create.tsx";
import { pixelText } from "./pixel-text.tsx";

export type BeMenuFieldProps<V extends string = string> = BeProps<
	"select",
	{
		/** Optional label drawn to the left of the well. */
		label?: ReactNode;
		/** Ellipsise the selected option. Needs `width` or a narrow ancestor to clip to. */
		truncate?: boolean;
		/** Width in --be-px units. A content measurement, so a raw count. */
		width?: number;
		value?: V;
		defaultValue?: V;
		/** The chosen option's value, typed from `value`/`defaultValue`. Fires after `onChange`. */
		onValueChange?: (value: V, event: ChangeEvent<HTMLSelectElement>) => void;
	}
>;

/**
 * The ref lands on the <select>, not the <label> -- see create.tsx.
 *
 * `style` NOW LANDS ON THE ROOT. It used to fall through `...rest` onto the
 * <select>, so `<BeMenuField style={{width}}>` sized the inner picker rather
 * than the field. That was a bug, and this is the behaviour change that fixes it.
 */
const MenuField = beComponent<BeMenuFieldProps, HTMLSelectElement>("BeMenuField", (props, be) => {
	const { label, scale, truncate, width, className, style, children, onChange, onValueChange, ...rest } = props;
	const { ref, ...root } = be("be-menu-field", truncate && "is-truncate");
	const css = width != null ? ({ ...root.style, "--be-menu-field-width": `calc(${width} * var(--be-px))` } as CSSProperties) : root.style;

	return (
		<label {...root} style={css}>
			{label != null && pixelText(label, "be-menu-field__label")}
			<span className="be-menu-field__well">
				<select
					className={truncate ? "be-menu-field__select be-truncate" : "be-menu-field__select"}
					ref={ref}
					onChange={(e) => {
						onChange?.(e);
						onValueChange?.(e.target.value, e);
					}}
					{...rest}
				>
					{children}
				</select>
				<span className="be-menu-field__arrow" aria-hidden="true" />
			</span>
		</label>
	);
});

// forwardRef erases the type parameter, so the export restores it (MUI and Carbon do the same)
export const BeMenuField = MenuField as (<V extends string = string>(
	props: BeMenuFieldProps<V> & RefAttributes<HTMLSelectElement>,
) => ReactElement | null) & { displayName?: string };
